import moment from "moment";

import { BTStatusBadge, type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

export interface IBTItemHeaderMetadataProps {
    /**
     * The status badge text (e.g., "Draft", "Sent", "Approved")
     */
    badgeText: string;

    /**
     * The display type for the BTStatusBadge component (e.g., "default", "warning", "success", "danger", "info")
     */
    badgeDisplayType: TBTStatusBadgeDisplayTypes;

    /**
     * The name of the user who created/modified the item. Must be paired with
     * `date` — the "by {displayName} at {date}" text is only rendered when
     * both are provided.
     */
    displayName?: string;

    /**
     * The date/time the item was created/modified. Must be paired with
     * `displayName` — the "by {displayName} at {date}" text is only rendered
     * when both are provided. Accepts a native Date or a moment.Moment.
     */
    date?: Date | moment.Moment;

    /**
     * Optional test ID for testing purposes
     */
    testid?: string;

    /**
     * Whether to show time in the date display
     * @default true
     */
    showTime?: boolean;

    /**
     * Optional CSS class name for custom styling
     */
    className?: string;
}

/**
 * BTItemHeader.Metadata is a sub-component that combines a status badge with
 * optional metadata text. It's designed to be used within BTItemHeader to
 * display consistent status and metadata information. The "by {displayName}
 * at {date}" text is composed internally and only rendered when both
 * `displayName` and `date` are provided — pass neither to show just the badge.
 *
 * @example Badge with attribution text
 * <BTItemHeader.Metadata
 *   badgeText="Draft"
 *   badgeDisplayType="default"
 *   displayName="John Greene"
 *   date={moment()}
 *   testid="item-metadata"
 * />
 *
 * @example Badge only
 * <BTItemHeader.Metadata
 *   badgeText="Draft"
 *   badgeDisplayType="default"
 *   testid="item-metadata"
 * />
 */
export const BTItemHeaderMetadata = ({
    badgeText,
    badgeDisplayType,
    displayName,
    date,
    testid,
    showTime = true,
    className,
}: IBTItemHeaderMetadataProps) => {
    const containerClassName = cn(
        "flex items-center whitespace-nowrap pt-2 md:pt-0 mb-1.25 min-w-0 bt-item-header-metadata",
        className,
    );

    const showAttributionText = displayName !== undefined && date !== undefined;

    return (
        <div className={containerClassName}>
            <BTStatusBadge
                testid={testid ? `${testid}-badge` : "metadata-badge"}
                className="badge-metadata"
                displayType={badgeDisplayType}
                textOnly
                text={badgeText}
            />
            {showAttributionText && (
                <TextTruncate
                    data-testid={testid ? `${testid}-text` : "metadata-text"}
                    className="ml-1"
                >
                    by {displayName} at{" "}
                    <DateDisplayDate
                        value={moment.isMoment(date) ? date.toDate() : date}
                        showTime={showTime}
                        data-testid={testid ? `${testid}-date` : "metadata-date"}
                    />
                </TextTruncate>
            )}
        </div>
    );
};
